import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, FileText, ClipboardCheck } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { HIPCAMP, RURAL_REVIEW_DATE } from "@/data/rural";

const STEP_CTA = {
  2: { label: "Download My Hosting Checklist", to: "/rural/checklist", icon: ClipboardCheck, testid: "hipcamp-cta-checklist" },
  5: { label: "Create My Guest Information Pack", to: "/rural/guest-pack", icon: FileText, testid: "hipcamp-cta-guestpack" },
};

const RuralHipcamp = () => (
  <div data-testid="rural-hipcamp-page">
    <Seo
      title="Your Land. Your Campsite. Your Opportunity."
      description="The beginner's guide to hosting campers on NSW rural land through Hipcamp — legal checks, property preparation, listing setup, finances and guest management."
    />
    <section className="bg-sand-light/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <Link to="/rural" data-testid="hipcamp-back" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
          <ArrowLeft className="w-4 h-4" /> Rural Land
        </Link>
        <FadeIn>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-teal tracking-tight">{HIPCAMP.title}</h1>
          <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">{HIPCAMP.lead}</p>
        </FadeIn>
      </div>
    </section>

    <section className="py-12 lg:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {HIPCAMP.steps.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.03}>
            <div className="bg-white border border-cloud p-7" data-testid={`hipcamp-step-${s.n}`}>
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-3xl text-sand">0{s.n}</span>
                <h2 className="font-serif text-xl sm:text-2xl text-teal">{s.title}</h2>
              </div>
              <ul className="mt-5 space-y-2.5">
                {s.points.map((p, j) => (
                  <li key={j} className="flex gap-3 text-sm text-charcoal/80 leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sand shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              {STEP_CTA[s.n] && (
                <Link
                  to={STEP_CTA[s.n].to}
                  data-testid={STEP_CTA[s.n].testid}
                  className="mt-6 inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-5 py-2.5 rounded-full hover:bg-teal-dark transition-colors"
                >
                  {(() => { const Icon = STEP_CTA[s.n].icon; return <Icon className="w-4 h-4" />; })()}
                  {STEP_CTA[s.n].label}
                </Link>
              )}
            </div>
          </FadeIn>
        ))}

        <FadeIn>
          <div className="bg-teal text-ivory p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <p className="font-serif text-xl">Ready to list?</p>
              <p className="mt-1 text-sm text-ivory/75 leading-relaxed">
                Host registration happens on Hipcamp — and remember, a listing is not development
                consent. Do the Step 1 checks first.
              </p>
            </div>
            <a
              href={HIPCAMP.hostUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hipcamp-host-link"
              className="inline-flex items-center gap-2 bg-ivory text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-sand-light transition-colors shrink-0"
            >
              Visit Hipcamp Host <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </FadeIn>

        <FadeIn>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/rural/estimator" data-testid="hipcamp-cta-estimator" className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors">
              Estimate my hosting income <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-charcoal/55">Legislative content last reviewed {RURAL_REVIEW_DATE}.</p>
          </div>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default RuralHipcamp;
